import { escolaBehavior } from './../../../models/escola.interface';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { User, userLogin } from '../../../models/authentication.interface';
import { Post, SingleResponse } from '../../../models/rest.interface';
import {
  Permissao,
  permissaoBehavior,
  PermissaoEnum,
  RoleEnum,
} from '../../../models/role.enum';
import {
  ConviteRequest,
  register,
  registerUser,
  resetPassword,
} from '../../../models/user.interface';
import { StorageServiceService } from '../../storage/storage-service.service';
import { invite } from '../../../models/convite.interface';
import { PaginacaoResponse } from '../../../models/paginacaoResponse.interface';

const URL = environment.BACKEND_API;
const URL_V2 = environment.BACKEND_API_V2;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  usuario = new BehaviorSubject<User | undefined>(undefined);
  permissao = new BehaviorSubject<permissaoBehavior | undefined>(
    this.storageService.obterPermissao(),
  );
  escola = new BehaviorSubject<escolaBehavior | undefined>(
    this.storageService.obterEscola(),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageServiceService,
  ) {}

  get user(): User {
    const userString = window.sessionStorage.getItem('user');

    return JSON.parse(userString as string);
  }

  get role(): RoleEnum | null {
    const userString = window.sessionStorage.getItem('user');

    if (userString) {
      const user: User = JSON.parse(userString);
      return this.convertNumberToRole(user.roleId);
    }

    return null;
  }

  get escolaSelecionada(): escolaBehavior | undefined {
    return this.escola.getValue() ?? this.storageService.obterEscola();
  }

  get permissaoRole(): permissaoBehavior | undefined {
    return this.permissao.getValue() ?? this.storageService.obterPermissao();
  }

  convertNumberToPermissao(role: number) {
    switch (role) {
      case 2:
        return PermissaoEnum.ROLE_DEFAULT;
      case 4:
        return PermissaoEnum.ROLE_ADMIN;
      case 6:
        return PermissaoEnum.ROLE_DIRETOR;
      case 8:
        return PermissaoEnum.ROLE_SECRETARIO;
      case 10:
        return PermissaoEnum.ROLE_PROFESSOR;
      case 12:
        return PermissaoEnum.ROLE_RESPONSAVEL;
      case 14:
        return PermissaoEnum.ROLE_SUPPORT;
      default:
        return PermissaoEnum.ROLE_DEFAULT;
    }
  }

  convertNumberToRole(role: number) {
    switch (role) {
      case 5:
        return RoleEnum.DIRETOR;
      case 4:
        return RoleEnum.ADMIN;
      case 6:
        return RoleEnum.SECRETARIO;
      case 3:
        return RoleEnum.PROFESSOR;
      case 7:
        return RoleEnum.RESPONSAVEL;
      default:
        return RoleEnum.RESPONSAVEL;
    }
  }

  buscarUserPorId(): Observable<User> {
    return this.http.get<User>(`${URL}/users/me`);
  }

  buscarUser(documentId: string): Observable<SingleResponse<userLogin>> {
    return this.http.get<SingleResponse<userLogin>>(
      `${URL}/users/?filters[documentId][$eq]=${documentId}&populate=escolas&populate=permissaos&populate=alunos`,
    );
  }

  listarUsuariosPorRole(idRole: number): Observable<PaginacaoResponse<any>> {
    return this.http.get<PaginacaoResponse<any>>(
      `${URL}/users/?filters[$and][0][permissaos][$eq]=${idRole}&filters[$and][1][escolas][$eq]=${this.escola.getValue()?.id as number}&populate=permissaos`,
    );
  }

  buscarUserPorAluno(idAluno: number): Observable<any> {
    return this.http.get<any>(
      `${URL}/users/?filters[$and][0][alunos][$eq]=${idAluno}&filters[$and][1][blocked][$eq]=false`,
    );
  }

  editarUser(request: any): Observable<any> {
    return this.http.put<any>(`${URL}/users/${this.user.id}`, request);
  }

  editarResponsavel(request: any, idUser: number): Observable<any> {
    return this.http.put<any>(`${URL}/users/${idUser}`, request);
  }

  desconectarUserEscola(
    escolaDocumentId: string,
    userDocumentId: string,
  ): Observable<any> {
    const body = {
      data: {
        users: {
          disconnect: [{documentId: userDocumentId}],
        },
      },
    };
    return this.http.put(`${URL}/escolas/${escolaDocumentId}`, body);
  }

  buscarUserPorEmail(email: string): Observable<any> {
    return this.http.get<any>(`${URL}/users/?filters[email][$eq]=${email}`);
  }

  buscarUserPorEmailPopulateAlunos(email: string): Observable<any> {
    return this.http.get<any>(
      `${URL}/users/?filters[email][$eq]=${email}&populate=alunos`,
    );
  }

  buscarUserPorNome(nome: string, idAluno: any): Observable<any> {
    return this.http.get<any>(
      `${URL}/users/?filters[$and][0][name][$containsi]=${nome}&filters[$and][1][alunos][$eq]=${idAluno}`,
    );
  }

  salvarUsuario(request: registerUser): Observable<any> {
    return this.http.post<any>(`${URL_V2}/users`, request);
  }

  registerUsuario(request: register): Observable<any> {
    return this.http.post<any>(`${URL_V2}/users`, request);
  }

  inviteUsuario(convite: Post<invite>): Observable<any> {
    return this.http.post(`${URL}/invites`, convite);
  }

  alterarSenha(changePassword: resetPassword): Observable<any> {
    return this.http.post(`${URL}/auth/change-password`, changePassword);
  }
}
